import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, formatINR, formatINRShort, formatDate, formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge, LotTag, PipelineTag, badgeStatus, stageKey } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { viewFor, canAccess } from "@/config/roleViews";
import { useLiveRefresh } from "@/lib/live";
import { ArrowLeft, Plus, CheckCircle2, Circle } from "lucide-react";
import PackingSlipEditor from "@/components/PackingSlipEditor";
import COAEditor from "@/components/COAEditor";
import MasterJobCardForm from "@/components/MasterJobCardForm";
import ProductionFlow from "@/components/ProductionFlow";
import JobMaterialsPanel from "@/components/JobMaterialsPanel";
import EditOrderInfoDialog from "@/components/EditOrderInfoDialog";
import EditPlanningSpecDialog from "@/components/orders/EditPlanningSpecDialog";
import BatchProductionRecord from "@/components/orders/BatchProductionRecord";

const STATUSES = ["pending", "in_production", "qc", "dispatched", "cancelled"];

// Workflow milestones in order
const MILESTONES = [
  { key: "date_received", label: "Order Received" },
  { key: "material_ordered_date", label: "Material Ordered" },
  { key: "bpr_issue_date", label: "BPR Issued" },
  { key: "cdr_received", label: "CDR Received" },
  { key: "hpdf_sent", label: "HPDF Sent" },
  { key: "hpdf_approved", label: "HPDF Approved" },
  { key: "plates_ordered", label: "Plates Ordered" },
  { key: "plates_received", label: "Plates Received" },
  { key: "shade_card_sent", label: "Shade Card Sent" },
  { key: "shade_card_approved", label: "Shade Card Approved" },
  { key: "die_ordered", label: "Die Ordered" },
  { key: "die_received", label: "Die Received" },
  { key: "printing_date", label: "Printing" },
  { key: "avt_date", label: "AVT" },
  { key: "flatbed_date", label: "Flatbed" },
  { key: "slitting_date", label: "Slitting" },
  { key: "packing_date", label: "Packing" },
  { key: "dispatch_date", label: "Dispatch" },
];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canMoney = viewFor(user).finance; // views without finance never see revenue / profit / P&L
  const canBrowseOrders = canAccess(user, "/orders"); // false for a role with no Orders/Jobs nav link (e.g. Printing)
  // A department floor role (printing_operator, avt_operator, ...) only has Production Flow access
  // on the server (routes/job_status.py ROLE_STAGES) — the other tabs (Materials, Dispatch, Packing
  // Slip, COA, Master Job Card, Production Logs) are either another department's or admin/office work,
  // so they don't belong on this role's tab bar at all. Admin and the plain core roles are unrestricted.
  const DEPT_ROLES = ["stores_keeper", "printing_operator", "avt_operator", "slitting_operator", "qc_inspector",
    "packing_operator", "dispatch_clerk"];
  const isDeptRole = DEPT_ROLES.includes(user?.role);
  // The open tab lives in the URL (?tab=), so links can land on it and a reload keeps it.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = isDeptRole
    ? ["overview", "planning", "bpr_record", "production_flow"]
    : ["overview", "planning", "bpr_record", "production_flow", "materials", "dispatch", "packing", "coa", "mjc", "production", ...(canMoney ? ["pl"] : [])];
  const activeTab = tabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const setActiveTab = (tab) => setSearchParams(tab === "overview" ? {} : { tab }, { replace: true });
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(null);
  const [ops, setOps] = useState([]);
  const [machines, setMachines] = useState([]);
  const [materials, setMaterials] = useState([]);
  // Full status list (pipeline statuses like "To be planned" included) so the picker never shows blank.
  const [statuses, setStatuses] = useState(STATUSES);
  useEffect(() => {
    api.get("/jobs/statuses").then(({ data }) => Array.isArray(data) && data.length && setStatuses(data)).catch(() => {});
  }, []);

  const load = () => api.get(`/jobs/${id}`)
    .then(({ data }) => { setJob(data); setMissing(null); })
    .catch((e) => e?.response?.status === 404 && setMissing(formatApiError(e)));

  useEffect(() => {
    setJob(null);
    load();
    api.get("/users").then(({ data }) => setOps(data.filter(u => ["operator", "production_manager"].includes(u.role)))).catch(() => {});
    api.get("/machines").then(({ data }) => setMachines(data)).catch(() => {});
    api.get("/inventory/materials").then(({ data }) => setMaterials(data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);
  // Stage saves, edits from other screens / users, stores issues (stores_issued_m) and
  // production logs all change what this page shows.
  const reload = useLiveRefresh(["jobs", "stores", "production", "customers"], load);

  if (missing) {
    return (
      <div className="p-8">
        <div className="border border-neutral-200 bg-white p-6 max-w-lg text-sm">
          <div className="font-medium">{missing}</div>
          <div className="text-neutral-500 mt-1">It may have been deleted by another user.</div>
          <Link to={canBrowseOrders ? "/orders" : "/"} className="inline-block mt-4 text-[#FF5A00] hover:underline">{canBrowseOrders ? "All jobs →" : "Dashboard →"}</Link>
        </div>
      </div>
    );
  }
  if (!job) return <div className="p-8 label-caps">Loading…</div>;

  // "Job ID only" — no BPR assigned yet (see the New Job form's "Save as Job ID"): show the
  // entry form as far as it's filled in, not the full tabbed view — Materials, Stores,
  // Production and the rest don't mean anything without a real BPR. Converting to BPR (inside
  // the form) sets job.mpwo_number, so this branch no longer matches on the next load and the
  // full view below takes over automatically.
  if (!job.mpwo_number) {
    return (
      <div>
        <PageHeader
          subtitle={<Link to={canBrowseOrders ? "/orders" : "/"} className="flex items-center gap-1 hover:text-[#FF5A00]"><ArrowLeft size={12} /> {canBrowseOrders ? "All Orders" : "Dashboard"}</Link>}
          title={job.job_number}
          actions={<StatusBadge status={badgeStatus(job)} />}
        />
        <div className="p-8">
          <div className="mb-4 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="jobid-only-banner">
            <b>To be planned — no BPR yet.</b> Fill in the rest of this job below, then use <b>Convert to BPR</b> to issue its BPR/MPWO number.
          </div>
          <MasterJobCardForm initial={job} onSaved={reload} />
        </div>
      </div>
    );
  }

  const setStatus = async (status) => {
    try { await api.patch(`/jobs/${id}`, { status }); toast.success("Status updated"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        subtitle={<Link to={canBrowseOrders ? "/orders" : "/"} className="flex items-center gap-1 hover:text-[#FF5A00]"><ArrowLeft size={12} /> {canBrowseOrders ? "All Orders" : "Dashboard"}</Link>}
        title={job.mpwo_number || job.job_number}
        actions={
          <div className="flex items-center gap-2">
            <LotTag job={job} />
            <PipelineTag job={job} />
            <StatusBadge status={badgeStatus(job)} />
            <Select value={job.status} onValueChange={setStatus}>
              <SelectTrigger data-testid="job-status-select" className="w-40 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...new Set([...statuses, ...(job.status ? [job.status] : [])])].map((s) => {
                  // Converted BPR is a real planning step, not just a label flip — it isn't offered until
                  // the Planning Spec tab actually has something in it (same bar ThinCardBanner uses).
                  const blocked = stageKey(s) === "converted_bpr" && !isPlanned(job);
                  return (
                    <SelectItem key={s} value={s} disabled={blocked} title={blocked ? "Fill in the Planning Spec tab first" : undefined}>
                      {s.replace(/_/g, " ")}{blocked ? " (fill Planning Spec first)" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <div className="p-8">
        {/* Top summary strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Kpi label="Customer" value={job.customer_name} />
          <Kpi label="Quantity" value={`${job.quantity?.toLocaleString("en-IN") || 0} ${job.unit || ""}`} mono />
          {canMoney && <Kpi label="Revenue" value={formatINRShort(job.revenue)} title={formatINR(job.revenue)} mono accent="text-neutral-900" />}
          {canMoney && <Kpi label="Profit" value={formatINRShort(job.profit)} title={formatINR(job.profit)} mono accent={job.profit >= 0 ? "text-emerald-700" : "text-rose-700"} />}
          <Kpi label="Due" value={formatDate(job.due_date || job.dispatch_date)} />
          <Kpi label="Type" value={job.job_type || "—"} />
        </section>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="rounded-sm bg-white border border-neutral-200">
            <TabsTrigger value="overview" className="rounded-sm" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="planning" className="rounded-sm" data-testid="tab-planning">Planning Spec</TabsTrigger>
            <TabsTrigger value="bpr_record" className="rounded-sm" data-testid="tab-bpr-record">Job Card</TabsTrigger>
            <TabsTrigger value="production_flow" className="rounded-sm" data-testid="tab-production-flow">Production Flow</TabsTrigger>
            {!isDeptRole && (
              <>
                <TabsTrigger value="materials" className="rounded-sm" data-testid="tab-materials">Materials</TabsTrigger>
                <TabsTrigger value="dispatch" className="rounded-sm" data-testid="tab-dispatch">Dispatch</TabsTrigger>
                <TabsTrigger value="packing" className="rounded-sm" data-testid="tab-packing">Packing Slip</TabsTrigger>
                <TabsTrigger value="coa" className="rounded-sm" data-testid="tab-coa">COA</TabsTrigger>
                <TabsTrigger value="mjc" className="rounded-sm" data-testid="tab-mjc">Master Job Card</TabsTrigger>
                <TabsTrigger value="production" className="rounded-sm" data-testid="tab-production">Production Logs</TabsTrigger>
                {canMoney && <TabsTrigger value="pl" className="rounded-sm" data-testid="tab-pl">P&L</TabsTrigger>}
              </>
            )}
          </TabsList>

          {/* ============================ OVERVIEW ============================ */}
          <TabsContent value="overview" className="mt-4">
            <section className="border border-neutral-200 bg-white">
              <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
                <span className="label-caps">Order Info</span>
                <EditOrderInfoDialog job={job} onSaved={reload} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 p-5 text-sm">
                <Field label="Customer" value={job.customer_name} />
                <Field label="Product" value={job.product_name} full />
                <Field label="MPWO" value={job.mpwo_number || "—"} mono />
                <Field label="Product Code" value={job.product_code || "—"} mono />
                <Field label="Old Code" value={job.old_code || "—"} mono />
                <Field label="SAP Code" value={job.sap_code || "—"} mono />
                <Field label="PO Number" value={job.po_number || "—"} mono />
                <Field label="PO Date" value={formatDate(job.po_date)} />
                <Field label="Order Confirmed By" value={job.order_confirmed_by || "—"} />
                <Field label="Date Received" value={formatDate(job.date_received)} />
                <Field label="BPR Issue Date" value={formatDate(job.bpr_issue_date)} />
                <Field label="Job Due Date" value={formatDate(job.due_date)} />
                <Field label="Quantity" value={`${job.quantity?.toLocaleString("en-IN")} ${job.unit || "NOS"}`} mono />
                <Field label="Job Type" value={job.job_type || "—"} />
                <Field label="Status" value={job.status} />
                <Field label="BPR Status" value={job.bpr_status || "—"} />
                <Field label="Delivery Location" value={job.delivery_location || "—"} full />
                <Field label="Delivery Area" value={job.delivery_area || "—"} />
                <Field label="Entry By" value={job.entry_by || job.bpr_issued_by || "—"} />
                <Field label="Notes / Remarks" value={job.notes || "—"} full />
              </div>
            </section>

            {/* ============================ CUSTOMER ADDRESSES ============================ */}
            {job.customer && (
              <section className="border border-neutral-200 bg-white mt-4" data-testid="customer-block">
                <div className="px-5 py-4 border-b border-neutral-200 label-caps flex items-center justify-between">
                  <span>Customer · {job.customer.party_name}{job.customer.party_code ? ` · ${job.customer.party_code}` : ""}</span>
                  <span className="text-[10px] text-neutral-400 normal-case tracking-normal">
                    {job.customer.gstin && <>GSTIN <span className="font-mono-num text-neutral-800">{job.customer.gstin}</span></>}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-neutral-200 text-sm">
                  <div className="p-5">
                    <div className="label-caps mb-2 text-[10px]">Corporate / Registered Address</div>
                    <div className="whitespace-pre-line text-neutral-800 leading-relaxed">
                      {job.customer.corporate_address || <span className="text-neutral-400 italic">Not set — edit customer to add</span>}
                    </div>
                    {(job.customer.contact_person || job.customer.phone || job.customer.email) && (
                      <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-600 space-y-0.5">
                        {job.customer.contact_person && <div>👤 {job.customer.contact_person}</div>}
                        {job.customer.phone && <div className="font-mono-num">📞 {job.customer.phone}</div>}
                        {job.customer.email && <div>✉︎ {job.customer.email}</div>}
                      </div>
                    )}
                  </div>
                  <div className="p-5 bg-amber-50/30" data-testid="delivery-address-block">
                    <div className="label-caps mb-2 text-[10px] text-amber-900">Delivery / Shipping Address</div>
                    <div className="whitespace-pre-line text-neutral-900 leading-relaxed font-medium">
                      {job.delivery_address || job.customer.delivery_address ||
                        <span className="text-neutral-400 italic font-normal">Not set — edit customer to add</span>}
                    </div>
                    {job.delivery_address && job.customer.delivery_address && job.delivery_address !== job.customer.delivery_address && (
                      <div className="mt-2 text-[11px] text-amber-700 italic">Job override — differs from customer master</div>
                    )}
                    <div className="mt-3 pt-3 border-t border-neutral-100 text-[11px] text-neutral-500">
                      Use this address on the packing slip and courier docket.
                    </div>
                  </div>
                </div>
              </section>
            )}
          </TabsContent>

          {/* ============================ PLANNING ============================ */}
          <TabsContent value="planning" className="mt-4 space-y-4">
            <div className="flex items-center justify-end">
              <EditPlanningSpecDialog job={job} onSaved={reload} />
            </div>
            <ThinCardBanner job={job} onFilled={reload} />
            <Section titleKey="section.label_dimensions" title="Label Dimensions & Die">
              <Field label="Label Length" value={job.label_length_mm ? `${job.label_length_mm} mm` : "—"} mono />
              <Field label="Label Width" value={job.label_width_mm ? `${job.label_width_mm} mm` : "—"} mono />
              <Field label="Die Size" value={job.die_size || "—"} mono />
              <Field label="Die Cutting" value={job.die_cutting || "—"} />
              <Field label="Die Box Number" value={job.die_box_number || "—"} mono />
              <Field label="Plate Box Number" value={job.plate_box_number || "—"} mono />
              <Field label="Core Diameter" value={job.core_diameter ? (String(job.core_diameter).match(/mm/i) ? job.core_diameter : `${job.core_diameter} mm`) : "—"} mono />
              <Field label="Cylinder" value={job.cylinder_mm ? `${job.cylinder_mm} mm` : "—"} mono />
              <Field label="Teeth" value={job.cylinder_teeth || job.teeth || "—"} mono />
            </Section>

            <Section titleKey="section.substrate_colors" title="Substrate & Colors">
              <Field label="Substrate Type" value={job.substrate_type || "—"} />
              <Field label="Substrate Code" value={job.substrate_code || "—"} mono />
              <Field label="Substrate Description" value={job.substrate_description || "—"} full />
              <Field label="GSM" value={job.gsm ? `${job.gsm} gsm` : "—"} mono />
              <Field label="Manufactured By" value={job.manufactured_by || job.supplier || "—"} />
              <Field label="Supplier" value={job.supplier || job.entry_by || "—"} />
              <Field label="No. of Colors" value={job.no_of_colors || "—"} mono />
              <Field label="Colour Details" value={job.colour_details || "—"} full />
              <Field label="Print Type" value={job.print_type || "—"} />
              <Field label="Color Matching" value={job.color_matching || "—"} />
              <Field label="Finishing" value={job.finishing_details || (job.roll_form ? "Roll Form" : job.sheet_form ? "Sheet Form" : "—")} />
              <Field label="Varnish" value={job.varnish_type || "—"} />
              <Field label="Embellishment" value={job.embellishment || "—"} />
              <Field label="Shade Card" value={job.shade_card || (job.shade_card_sent ? "Sent" : "—")} />
              <Field label="Form of Supply" value={job.form_of_supply || (job.roll_form && job.sheet_form ? "Roll + Sheet" : job.roll_form ? "Roll Form" : job.sheet_form ? "Sheet Form" : "—")} />
            </Section>

            <Section titleKey="section.availability" title="Availability Check">
              <AvailBadge label="Plate" value={job.plate_available} />
              <AvailBadge label="Die" value={job.die_available} />
              <AvailBadge label="Material" value={job.material_available} />
              <AvailBadge label="Ink" value={job.ink_available} />
            </Section>

            <Section titleKey="section.layout_planned" title="Layout & Planned Values">
              <Field label="Labels / Roll" value={job.labels_per_roll?.toLocaleString("en-IN") || "—"} mono />
              <Field label="Labels / Sheet" value={job.labels_per_sheet || "—"} mono />
              <Field label="Ups Across" value={job.ups_across || "—"} mono />
              <Field label="Ups Along" value={job.ups_along || "—"} mono />
              <Field label="No. of Ups Planned / Repeat" value={job.no_of_ups || job.total_ups_per_repeat || "—"} mono />
              <Field label="Along Gap" value={job.along_gap_mm ? `${job.along_gap_mm} mm` : (job.between_gap ? `${job.between_gap} mm` : "—")} mono />
              <Field label="Stock Width" value={job.stock_width_mm ? `${job.stock_width_mm} mm` : (job.paper_width_mm ? `${job.paper_width_mm} mm` : "—")} mono />
              <Field label="Planned Good" value={job.planned_good_meter ? `${job.planned_good_meter} m` : "—"} mono />
              <Field label="Planned Waste" value={job.planned_waste_meter ? `${job.planned_waste_meter} m` : "—"} mono />
              <Field label="Excess Qty" value={job.excess_qty || "—"} mono />
              <Field label="Makeready" value={job.makeready_hours ? `${job.makeready_hours} hr` : "—"} mono />
              <Field label="Direction" value={job.direction || job.winding_instructions || (job.lo_li_ticks && Object.entries(job.lo_li_ticks).filter(([, v]) => v).map(([k]) => k).join(", ")) || "—"} />
              <Field label="Numbering" value={(() => {
                const raw = job.numbering || job.serial_numbering;
                if (!raw) return "—";
                const s = String(raw).toLowerCase();
                if (s === "yes") return "Required";
                if (s === "no") return "Not Required";
                return raw;
              })()} />
            </Section>

            {(job.spl_instruction_1 || job.spl_instruction_2 || job.spl_instruction_3) && (
              <Section title="Special Instructions">
                {job.spl_instruction_1 && <Field label="Instruction 1" value={job.spl_instruction_1} full />}
                {job.spl_instruction_2 && <Field label="Instruction 2" value={job.spl_instruction_2} full />}
                {job.spl_instruction_3 && <Field label="Instruction 3" value={job.spl_instruction_3} full />}
                {job.winding_instructions && <Field label="Winding" value={job.winding_instructions} full />}
                {job.lo_li_ticks && Object.values(job.lo_li_ticks || {}).some(Boolean) && (
                  <Field
                    label="Winding Direction"
                    value={Object.entries(job.lo_li_ticks || {}).filter(([, v]) => v).map(([k]) => k).join(", ")}
                    full
                  />
                )}
              </Section>
            )}
            {job.lo_li_ticks && Object.values(job.lo_li_ticks || {}).some(Boolean) && !(job.spl_instruction_1 || job.spl_instruction_2 || job.spl_instruction_3 || job.winding_instructions) && (
              <Section title="Special Instructions">
                <Field
                  label="Winding Direction"
                  value={Object.entries(job.lo_li_ticks || {}).filter(([, v]) => v).map(([k]) => k).join(", ")}
                  full
                />
              </Section>
            )}
          </TabsContent>

          {/* ============================ BATCH PRODUCTION RECORD ============================ */}
          <TabsContent value="bpr_record" className="mt-4">
            <BatchProductionRecord job={job} onSaved={reload} />
          </TabsContent>

          {/* ============================ PRODUCTION FLOW ============================ */}
          <TabsContent value="production_flow" className="mt-4">
            <ProductionFlow key={job.id} jobId={job.id} initialStageKey={searchParams.get("stage")} />
          </TabsContent>

          {/* ============================ MATERIALS (Stores I/O against this job) ============================ */}
          <TabsContent value="materials" className="mt-4">
            <JobMaterialsPanel job={job} />
          </TabsContent>

          {/* ============================ DISPATCH ============================ */}
          <TabsContent value="dispatch" className="mt-4">
            <Section titleKey="section.dispatch_details" title="Dispatch Details">
              <Field label="Invoice Number" value={job.invoice_number || "—"} mono />
              <Field label="Dispatch Date" value={formatDate(job.dispatch_date)} />
              <Field label="Dispatch Qty" value={job.dispatch_qty?.toLocaleString("en-IN") || "—"} mono />
              <Field label="No. of Boxes" value={job.no_of_boxes || "—"} mono />
              <Field label="Vehicle No." value={job.vehicle_number || "—"} mono />
              <Field label="Transport" value={job.transport || "—"} />
              <Field label="Rolls Details" value={job.rolls_details || "—"} full />
              <Field label="Location" value={job.location || job.delivery_location || "—"} full />
              <Field label="QA/QC Recon." value={job.qa_qc_reconciliation || "—"} />
            </Section>
          </TabsContent>

          {/* ============================ PACKING SLIP ============================ */}
          <TabsContent value="packing" className="mt-4">
            <PackingSlipEditor job={job} onSaved={reload} />
          </TabsContent>

          {/* ============================ COA ============================ */}
          <TabsContent value="coa" className="mt-4">
            <COAEditor job={job} />
          </TabsContent>

          {/* ============================ MASTER JOB CARD ============================ */}
          <TabsContent value="mjc" className="mt-4">
            <MasterJobCardForm
              initial={job}
              mode={(job.status || "").toLowerCase() === "enquiry" ? "inquiry" : "job"}
              onSaved={reload}
            />
          </TabsContent>

          {/* ============================ PRODUCTION ============================ */}
          <TabsContent value="production" className="mt-4 space-y-4">
            <section className="border border-neutral-200 bg-white">
              <div className="px-5 py-4 border-b border-neutral-200 flex justify-between items-center">
                <div className="label-caps">Production Logs</div>
                <LogDialog jobId={id} ops={ops} machines={machines} onDone={reload} />
              </div>
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 label-caps">
                  <tr>
                    <th className="text-left px-4 py-2.5">Operator</th>
                    <th className="text-left px-4 py-2.5">Machine</th>
                    <th className="text-left px-4 py-2.5">Start</th>
                    <th className="text-left px-4 py-2.5">End</th>
                    <th className="text-right px-4 py-2.5">Output</th>
                    <th className="text-right px-4 py-2.5">Waste</th>
                    <th className="text-right px-4 py-2.5">Labour ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {(job.production_logs || []).map((l) => (
                    <tr key={l.id} className="border-t border-neutral-100 data-row">
                      <td className="px-4 py-2.5">{l.operator_name}</td>
                      <td className="px-4 py-2.5">{l.machine_name}</td>
                      <td className="px-4 py-2.5 font-mono-num text-xs">{new Date(l.start_time).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-2.5 font-mono-num text-xs">{new Date(l.end_time).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num">{l.output_qty}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num text-rose-700">{l.waste_qty}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(l.labour_cost)}</td>
                    </tr>
                  ))}
                  {(!job.production_logs || job.production_logs.length === 0) && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-neutral-400 text-sm">No production logs yet.</td></tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="border border-neutral-200 bg-white">
              <div className="px-5 py-4 border-b border-neutral-200 flex justify-between items-center">
                <div className="label-caps">Materials Consumed</div>
                <MaterialDialog jobId={id} materials={materials} onDone={reload} />
              </div>
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 label-caps">
                  <tr>
                    <th className="text-left px-4 py-2.5">Material</th>
                    <th className="text-right px-4 py-2.5">Qty</th>
                    <th className="text-right px-4 py-2.5">Cost</th>
                    <th className="text-left px-4 py-2.5">When</th>
                  </tr>
                </thead>
                <tbody>
                  {(job.materials_used || []).map((m) => (
                    <tr key={m.id} className="border-t border-neutral-100 data-row">
                      <td className="px-4 py-2.5">{m.material_name}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num">{m.quantity} {m.unit}</td>
                      <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(m.cost)}</td>
                      <td className="px-4 py-2.5 font-mono-num text-xs">{new Date(m.recorded_at).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                  {(!job.materials_used || job.materials_used.length === 0) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-neutral-400 text-sm">No materials logged.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          </TabsContent>

          {/* ============================ P&L ============================ */}
          {canMoney && <TabsContent value="pl" className="mt-4">
            <section className="border border-neutral-200 bg-white p-5 max-w-md" data-testid="pl-panel">
              <div className="label-caps mb-4">Profit & Loss</div>
              <PLRow label="Revenue" value={formatINR(job.revenue)} />
              <PLRow label="Material Cost" value={`− ${formatINR(job.material_cost)}`} />
              <PLRow label="Labour Cost" value={`− ${formatINR(job.labour_cost)}`} />
              <div className="border-t border-neutral-200 my-3"></div>
              <PLRow label="Total Cost" value={formatINR(job.total_cost)} />
              <PLRow label="Profit" value={formatINR(job.profit)} accent={job.profit >= 0 ? "text-emerald-700" : "text-rose-700"} bold />
              <div className="mt-3 flex items-center justify-between">
                <span className="label-caps">Margin</span>
                <span className={`font-mono-num text-sm font-bold ${job.margin_pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{job.margin_pct}%</span>
              </div>
              <div className="mt-2 flex items-center justify-between" data-testid="pl-rm-pct">
                <span className="label-caps">Raw Material % of Sales</span>
                <span className={`font-mono-num text-sm font-bold ${
                  !job.rm_pct ? "text-neutral-500" :
                  job.rm_pct > 70 ? "text-rose-700" :
                  job.rm_pct > 50 ? "text-amber-700" : "text-emerald-700"
                }`}>{job.rm_pct ? `${job.rm_pct}%` : "—"}</span>
              </div>
              <div className="border-t border-neutral-200 my-3"></div>
              <div className="mt-3 space-y-1.5">
                <PLRow label="Order Qty" value={`${job.quantity?.toLocaleString("en-IN")} ${job.unit || ""}`} />
                <PLRow label="Total Output" value={`${job.total_output?.toLocaleString("en-IN") || 0} units`} />
                <PLRow label="Waste" value={`${job.total_waste?.toLocaleString("en-IN") || 0} units`} accent="text-rose-700" />
              </div>
            </section>
          </TabsContent>}
        </Tabs>
      </div>
    </div>
  );
}

function Kpi({ label, value, title, mono, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-3">
      <div className="label-caps text-[10px]">{label}</div>
      <div className={`mt-1 text-sm truncate ${mono ? "font-mono-num font-semibold" : "font-medium"} ${accent || "text-neutral-900"}`} title={title || value}>
        {value || "—"}
      </div>
    </div>
  );
}

const SPEC_FIELDS = ["substrate_code", "substrate_type", "gsm", "no_of_colors", "print_type", "varnish_type", "finishing_details", "form_of_supply", "labels_per_roll", "die_size", "ups_across", "core_diameter", "colour_details", "cylinder_mm"];
const have = (v) => v !== null && v !== undefined && v !== "" && v !== "NA" && v !== 0;
// A job counts as planned (its Planning Spec is filled in) once at least 5 of SPEC_FIELDS have a real
// value — same threshold ThinCardBanner uses to flag "no planning details". Below that, Converted BPR
// is not offered on the status picker: the planning sheet has to be filled in first.
const isPlanned = (job) => SPEC_FIELDS.filter((k) => have(job[k])).length >= 5;

// A job whose planning details are empty (loaded from a register without them): offer the details of the same product's
// other jobs, empty fields only.
function ThinCardBanner({ job, onFilled }) {
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const thin = !isPlanned(job);
  useEffect(() => {
    if (!thin || !job.customer_name || !(job.product_code || job.product_name)) { setFound(null); return; }
    api.get("/jobs/lookup/last-card", { params: { customer_name: job.customer_name, product_code: job.product_code || undefined, product_name: job.product_name || undefined } })
      .then(({ data }) => setFound(data.found ? data : { none: true })).catch(() => setFound(null));
  }, [job.id, thin]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!thin) return null;
  const available = found?.card ? Object.keys(found.card).filter((k) => !["product_code", "card_title"].includes(k) && !have(job[k])).length : 0;
  const fill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/fill-card`);
      toast.success(data.filled ? `${data.filled} details filled from ${data.from}` : "Nothing to fill");
      onFilled?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm flex flex-wrap items-center gap-3" data-testid="thin-card-banner">
      <div className="flex-1 min-w-[16rem]">
        <b>This job has no planning details.</b>{" "}
        {available > 0
          ? <>{available} are available from {found.jobs_used === 1 ? "another job" : `${found.jobs_used} jobs`} of the same product (latest {found.source_mpwo}).</>
          : <>No other job of this product has them either. They come with a Planning sheet import, or from the job's Master Job Card tab.</>}
      </div>
      {available > 0 && <Button size="sm" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]" disabled={busy} onClick={fill} data-testid="thin-card-fill">{busy ? "Filling…" : "Fill the empty fields"}</Button>}
    </div>
  );
}

function Section({ title, titleKey, children }) {
  return (
    <section className="border border-neutral-200 bg-white">
      <div className="px-5 py-4 border-b border-neutral-200 label-caps">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 p-5 text-sm">{children}</div>
    </section>
  );
}

function Field({ label, value, mono, full }) {
  return (
    <div className={full ? "col-span-2 md:col-span-3" : ""}>
      <div className="label-caps mb-1">{label}</div>
      <div className={`text-neutral-900 break-words ${mono ? "font-mono-num" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function AvailBadge({ label, value }) {
  const v = (value || "").toLowerCase();
  const isYes = v === "yes" || v.includes("avail");
  const isNo = v === "no" || v.includes("wait");
  const isNA = v === "na" || v.includes("n/a");
  const cls = isYes
    ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
    : isNo
      ? "bg-rose-100 text-rose-800 border border-rose-200"
      : isNA
        ? "bg-neutral-100 text-neutral-600 border border-neutral-200"
        : "bg-neutral-100 text-neutral-400 border border-neutral-200";
  return (
    <div>
      <div className="label-caps mb-1">{label}</div>
      <span className={`text-xs px-2 py-0.5 rounded-sm uppercase tracking-widest ${cls}`}>{value || "—"}</span>
    </div>
  );
}

function PLRow({ label, value, accent, bold }) {
  return (
    <div className="flex justify-between items-center py-1.5">
      <span className="text-sm text-neutral-600">{label}</span>
      <span className={`font-mono-num text-sm ${bold ? "font-bold" : ""} ${accent || "text-neutral-900"}`}>{value}</span>
    </div>
  );
}

function LogDialog({ jobId, ops, machines, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ operator_id: "", machine_id: "", start_time: "", end_time: "", output_qty: 0, waste_qty: 0, setup_waste: "", lsd_meters: "", labour_cost: 0, notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        job_id: jobId, ...f,
        output_qty: Number(f.output_qty),
        waste_qty: Number(f.waste_qty),
        labour_cost: Number(f.labour_cost),
        setup_waste: f.setup_waste === "" ? null : Number(f.setup_waste),
        lsd_meters: f.lsd_meters === "" ? null : Number(f.lsd_meters),
      };
      await api.post("/production/log", payload);
      toast.success("Log added"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-log-btn" size="sm" variant="outline" className="rounded-sm"><Plus size={12} className="mr-1" />Log Run</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle className="font-display">Production Log</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label-caps block mb-1.5">Operator</label>
            <Select value={f.operator_id} onValueChange={(v) => setF({ ...f, operator_id: v })}>
              <SelectTrigger data-testid="log-operator" className="rounded-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{ops.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} · {o.role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="label-caps block mb-1.5">Machine</label>
            <Select value={f.machine_id} onValueChange={(v) => setF({ ...f, machine_id: v })}>
              <SelectTrigger data-testid="log-machine" className="rounded-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="label-caps block mb-1.5">Start</label>
            <Input data-testid="log-start" type="datetime-local" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className="rounded-sm" required /></div>
          <div><label className="label-caps block mb-1.5">End</label>
            <Input data-testid="log-end" type="datetime-local" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className="rounded-sm" required /></div>
          <div><label className="label-caps block mb-1.5">Good Output</label>
            <Input data-testid="log-output" type="number" value={f.output_qty} onChange={(e) => setF({ ...f, output_qty: e.target.value })} className="rounded-sm" /></div>
          <div><label className="label-caps block mb-1.5">Waste Qty (total)</label>
            <Input data-testid="log-waste" type="number" value={f.waste_qty} onChange={(e) => setF({ ...f, waste_qty: e.target.value })} className="rounded-sm" /></div>
          <div className="col-span-2 pt-1 border-t border-neutral-100">
            <div className="label-caps text-[10px] text-neutral-500 mb-2">Wastage breakdown (meters) — optional, feeds Operator Output report</div>
          </div>
          <div><label className="label-caps block mb-1.5">Setup Waste (m)</label>
            <Input data-testid="log-setup-waste" type="number" step="0.01" value={f.setup_waste} onChange={(e) => setF({ ...f, setup_waste: e.target.value })} className="rounded-sm" placeholder="e.g. 800" /></div>
          <div><label className="label-caps block mb-1.5">LSD Meters (m)</label>
            <Input data-testid="log-lsd-meters" type="number" step="0.01" value={f.lsd_meters} onChange={(e) => setF({ ...f, lsd_meters: e.target.value })} className="rounded-sm" placeholder="e.g. 150" /></div>
          <div className="col-span-2"><label className="label-caps block mb-1.5">Labour Cost (₹)</label>
            <Input type="number" value={f.labour_cost} onChange={(e) => setF({ ...f, labour_cost: e.target.value })} className="rounded-sm" /></div>
          <DialogFooter className="col-span-2"><Button data-testid="log-submit" type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MaterialDialog({ jobId, materials: all, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ material_id: "", quantity: 0, cost: 0 });
  // Paper and film come from the stores ledger and are issued on the Materials tab.
  const materials = all.filter((m) => m.stock_source !== "ledger");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/production/material-usage", { job_id: jobId, ...f, quantity: Number(f.quantity), cost: Number(f.cost) });
      toast.success("Material logged"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-material-btn" size="sm" variant="outline" className="rounded-sm"><Plus size={12} className="mr-1" />Consume</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle className="font-display">Consume Material</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label-caps block mb-1.5">Material</label>
            <Select value={f.material_id} onValueChange={(v) => setF({ ...f, material_id: v })}>
              <SelectTrigger data-testid="mat-select" className="rounded-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · stock {m.current_stock}{m.unit}</SelectItem>)}
                {materials.length === 0 && <SelectItem value="none" disabled>No consumables set up</SelectItem>}
              </SelectContent>
            </Select>
            <p className="mt-1.5 text-[11px] text-neutral-500">Paper and film are issued by stores on the job's Materials tab.</p>
          </div>
          <div><label className="label-caps block mb-1.5">Quantity</label>
            <Input type="number" step="0.01" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} className="rounded-sm" required /></div>
          <div><label className="label-caps block mb-1.5">Cost Override (₹) — 0 to auto-calc</label>
            <Input type="number" step="0.01" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} className="rounded-sm" /></div>
          <DialogFooter><Button data-testid="mat-submit" type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">Consume</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
